/**
 * The single sanctioned source of randomness.
 *
 * Everything procedural in the audio layer takes a seeded generator. That is
 * what makes the ambient bed reproducible (AC.17) and what lets tests compare
 * rendered PCM across runs.
 *
 * `Math.random()` is lint-banned under `src/audio/**`; this module is the
 * exempt implementation it points at.
 */

/** FNV-1a, 32-bit. Stable across runs and platforms. */
export function hash32(input: string | number): number {
  const text = String(input);
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** Mulberry32: small, fast, well-distributed, and trivially reproducible. */
export function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return function next(): number {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface Rng {
  next(): number;
  range(min: number, max: number): number;
  int(min: number, maxExclusive: number): number;
  pick<T>(items: readonly T[]): T;
  chance(probability: number): boolean;
}

export function createRng(seed: number | string): Rng {
  const value = typeof seed === "number" ? seed >>> 0 : hash32(seed);
  const next = mulberry32(value);
  return {
    next,
    range(min, max) {
      return min + next() * (max - min);
    },
    int(min, maxExclusive) {
      if (maxExclusive <= min) return min;
      return min + Math.floor(next() * (maxExclusive - min));
    },
    pick<T>(items: readonly T[]): T {
      if (items.length === 0) throw new Error("cannot pick from an empty list");
      return items[Math.floor(next() * items.length)] as T;
    },
    chance(probability) {
      return next() < probability;
    },
  };
}
